import { GridRowClassNameParams } from '@mui/x-data-grid-pro';
import {
  CasinoBetRow,
  CasinoBetTotals,
  CasinoCategoryUpdatePayload,
  CasinoGameApiItem,
  CasinoGameUpdatePayload,
  CasinoMarketplace,
  CasinoSummaryFilters,
  CasinoSummaryRow,
  CasinoSummaryTotals,
  CasinoTransactionsFilters,
  GgaFreeSpinsFilters,
} from '../@types/casino';
import { UserRole } from '../@types/api';
import { USER_ROLES } from '../constants/keycloak';

export const CASINO_REPORT_ROLES = {
  MANAGER: 'manager',
  AGENT: 'agent',
  PLAYER: 'player',
} as const;

export const isCasinoAggregatorEnabled = (marketplace: CasinoMarketplace | undefined, aggregator: string): boolean => {
  const configuredAggregators =
    marketplace?.aggregators ??
    marketplace?.activeAggregators ??
    (marketplace?.aggregator ? [marketplace.aggregator] : undefined) ??
    (marketplace?.activeAggregator ? [marketplace.activeAggregator] : []);

  return configuredAggregators.some((configured) => configured.toUpperCase() === aggregator.toUpperCase());
};

export type CasinoReportRole = (typeof CASINO_REPORT_ROLES)[keyof typeof CASINO_REPORT_ROLES];

export type CasinoFreeSpinsAvailability =
  { status: 'available'; aggregator: 'GGA' | 'GOLDGAMES' } | { status: 'disabled' | 'configuration-required' };

export const getCasinoFreeSpinsAvailability = (
  marketplace: CasinoMarketplace | undefined
): CasinoFreeSpinsAvailability => {
  const enabled = ['GGA', 'GOLDGAMES'].filter((name) => isCasinoAggregatorEnabled(marketplace, name));
  if (!enabled.length) return { status: 'disabled' };

  const selected = marketplace?.freeSpinsAggregator;
  const aggregator = selected || (enabled.length === 1 ? enabled[0] : undefined);
  if ((aggregator === 'GGA' || aggregator === 'GOLDGAMES') && enabled.includes(aggregator)) {
    return { status: 'available', aggregator };
  }

  return { status: 'configuration-required' };
};

export const getCasinoFreeSpinsAggregator = (marketplace: CasinoMarketplace | undefined) => {
  const availability = getCasinoFreeSpinsAvailability(marketplace);
  return availability.status === 'available' ? availability.aggregator : undefined;
};

const ADMIN_LIKE_CASINO_REPORT_ROLES: UserRole[] = [
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.ADMIN,
  USER_ROLES.TECH_SUPPORT,
  USER_ROLES.TRADER,
];

export const getDefaultCasinoReportRole = (userRole?: UserRole): CasinoReportRole => {
  if (userRole === USER_ROLES.MANAGER) return CASINO_REPORT_ROLES.AGENT;
  if (userRole === USER_ROLES.AGENT || userRole === USER_ROLES.AFFILIATE) return CASINO_REPORT_ROLES.PLAYER;

  return CASINO_REPORT_ROLES.MANAGER;
};

export const getCasinoReportRoleOptions = (userRole?: UserRole): CasinoReportRole[] => {
  if (ADMIN_LIKE_CASINO_REPORT_ROLES.includes(userRole as UserRole)) {
    return [CASINO_REPORT_ROLES.MANAGER, CASINO_REPORT_ROLES.AGENT, CASINO_REPORT_ROLES.PLAYER];
  }

  if (userRole === USER_ROLES.MANAGER) {
    return [CASINO_REPORT_ROLES.AGENT, CASINO_REPORT_ROLES.PLAYER];
  }

  return [CASINO_REPORT_ROLES.PLAYER];
};

export const canFilterCasinoReportsByManager = (userRole: UserRole | undefined, reportRole?: string) => {
  if (!ADMIN_LIKE_CASINO_REPORT_ROLES.includes(userRole as UserRole)) return false;

  return (
    reportRole === CASINO_REPORT_ROLES.MANAGER ||
    reportRole === CASINO_REPORT_ROLES.AGENT ||
    reportRole === CASINO_REPORT_ROLES.PLAYER
  );
};

export const canFilterCasinoReportsByAgent = (userRole: UserRole | undefined, reportRole?: string) => {
  if (userRole === USER_ROLES.AGENT || userRole === USER_ROLES.AFFILIATE) return false;

  return reportRole === CASINO_REPORT_ROLES.AGENT || reportRole === CASINO_REPORT_ROLES.PLAYER;
};

export const canFilterCasinoReportsByPlayer = (reportRole?: string) => reportRole === CASINO_REPORT_ROLES.PLAYER;

export const getCasinoTotalsRowClass = (params: GridRowClassNameParams) => (params.row.isTotalsRow ? 'totals' : '');

export const formatCasinoPercentage = (value: unknown) => {
  if (typeof value !== 'number') return '';

  return `${(value * 100).toFixed(2)}%`;
};

export const appendCasinoTotalsRow = <TRow>(items: TRow[], totalsRow?: TRow) =>
  totalsRow ? [...items, totalsRow] : items;

type CasinoCategoryUpdateSource = Partial<
  CasinoCategoryUpdatePayload & {
    _id?: string;
    id?: string;
    marketplaceId?: string;
  }
>;

const CATEGORY_UPDATE_KEYS: (keyof CasinoCategoryUpdatePayload)[] = [
  'favourite',
  'gameIds',
  'locale',
  'name',
  'position',
  'providerCategoryName',
];

export const buildCasinoCategoryUpdatePayload = (
  data: CasinoCategoryUpdateSource
): Partial<CasinoCategoryUpdatePayload> => {
  return CATEGORY_UPDATE_KEYS.reduce<Partial<CasinoCategoryUpdatePayload>>((payload, key) => {
    const value = data[key];
    if (value !== undefined) {
      Object.assign(payload, { [key]: value });
    }
    return payload;
  }, {});
};

type CasinoGameUpdateSource = Partial<CasinoGameApiItem & { id?: string; marketplaceConfig?: unknown }>;

const GAME_UPDATE_KEYS: (keyof CasinoGameUpdatePayload)[] = [
  'categoryId',
  'categoryName',
  'demoAvailable',
  'frbAvailable',
  'imageURL',
  'name',
  'position',
  'providerGamePosition',
  'providerName',
];

export const buildCasinoGameUpdatePayload = (data: CasinoGameUpdateSource): Partial<CasinoGameUpdatePayload> => {
  return GAME_UPDATE_KEYS.reduce<Partial<CasinoGameUpdatePayload>>((payload, key) => {
    const value = data[key];
    if (value !== undefined) {
      Object.assign(payload, { [key]: value });
    }
    return payload;
  }, {});
};

export const buildCasinoGamePositionUpdatePayload = ({
  position,
}: {
  position: number | null;
}): Partial<CasinoGameUpdatePayload> => buildCasinoGameUpdatePayload({ position });

export const buildCasinoBetsTotalsRow = (totals?: CasinoBetTotals): CasinoBetRow | undefined => {
  if (!totals) return undefined;

  return {
    id: 'totals',
    _id: 'totals',
    createdAt: 'Totals',
    payInAmount: totals.totalPayInAmount,
    payOutAmount: totals.totalPayOutAmount,
    percentageGain: totals.totalPercentageGain,
    isTotalsRow: true,
  };
};

export const buildCasinoSummaryTotalsRow = (totals?: CasinoSummaryTotals): CasinoSummaryRow | undefined => {
  if (!totals) return undefined;

  return {
    id: 'totals',
    user: { username: 'Totals' },
    totalDistributedBonusAmount: totals.totalDistributedBonusAmount,
    jackpotAmount: totals.jackpotAmount,
    ngr: totals.ngr,
    totalPaidInAmount: totals.totalPaidInAmount,
    totalPaidOutAmount: totals.totalPaidOutAmount,
    totalProfit: totals.totalProfit,
    isTotalsRow: true,
  };
};

type CasinoRoleFilterFields = {
  role?: string;
  managerIds?: string[];
  agentIds?: string[];
  playerIds?: string[];
};

const prepareCasinoRoleFilterFields = <T extends Partial<CasinoRoleFilterFields>>(data: T): T => {
  const nextData = { ...data };

  // Role controls which hierarchy filters are visible. Drop hidden ids so stale form state cannot leak to casino-api.
  if (nextData.role === CASINO_REPORT_ROLES.MANAGER) {
    delete nextData.agentIds;
    delete nextData.playerIds;
  }

  if (nextData.role === CASINO_REPORT_ROLES.AGENT) {
    delete nextData.playerIds;
  }

  if (!nextData.managerIds?.length) delete nextData.managerIds;
  if (!nextData.agentIds?.length) delete nextData.agentIds;
  if (!nextData.playerIds?.length) delete nextData.playerIds;

  return nextData;
};

export const prepareCasinoReportFilters = <T extends Partial<CasinoSummaryFilters>>(data: T): T => {
  const nextData = prepareCasinoRoleFilterFields(data);

  if (!nextData.entityId) delete nextData.entityId;
  if (!nextData.providerName) delete nextData.providerName;
  if (!nextData.gameName) delete nextData.gameName;

  return nextData;
};

export const prepareCasinoTransactionFilters = <T extends Partial<CasinoTransactionsFilters>>(data: T): T => {
  const nextData = prepareCasinoRoleFilterFields(data);

  // Transactions need the role only for UI visibility; casino-api accepts the id filters, not this UI field.
  delete nextData.role;

  return nextData;
};

export const prepareGgaFreeSpinsFilters = <T extends Partial<GgaFreeSpinsFilters>>(data: T): T => {
  // Discard status selections saved before the filter was removed.
  const nextData: T & { status?: unknown } = { ...data };
  delete nextData.status;

  if (!nextData.playerIds?.length) delete nextData.playerIds;

  return nextData;
};
